import { addMonths, startOfMonth } from '../../helpers/date'
import checkPermission from '../../helpers/permissions/checkPermission'
import { PermissionSlug } from '../../types/permissionTypes'
import { Permission } from '../Permission/Permission'
import {
    ValkTalkFeedType,
    ValkTalkPost,
    ValkTalkPostAction,
    ValkTalkPostFormData,
    ValkTalkPostsQuery,
} from './ValkTalkPost'

export const VALK_FEED_GROUP = 'valk'
export const HOTEL_FEED_GROUP = 'hotel'

export const VALK_FEED_ID = 'global'

// The back-end's hotel feed for acceptance, named after the acceptance tenant's hotel instead of the hostname
export const ACCEPTANCE_HOTEL_FEED_ID = 'cdemo_stag_aker'

export const VALK_TALK_POST_IMAGE_MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

// Per feed, per month. Plenty for now; the list shows a notice when a feed has more.
export const VALK_TALK_POSTS_QUERY_LIMIT = 100

export const VALK_TALK_POST_TITLE_FALLBACK_LENGTH = 80

// The employee app's (Dutch) newsfeed route. It needs to read `post` from the query string to focus the post.
export const EMPLOYEE_APP_NEWSFEED_PATH = 'communicatie/nieuwsfeed'

// Valkplanner checks these per feed, for every post on it: an edit or delete permission isn't limited to own posts
export const feedTypePermissions: Record<ValkTalkPostAction, Record<ValkTalkFeedType, PermissionSlug>> = {
    [ValkTalkPostAction.add]: {
        [ValkTalkFeedType.valk]: 'add-valk-feed-message',
        [ValkTalkFeedType.hotel]: 'add-hotel-feed-message',
    },
    [ValkTalkPostAction.edit]: {
        [ValkTalkFeedType.valk]: 'edit-valk-feed-message',
        [ValkTalkFeedType.hotel]: 'edit-hotel-feed-message',
    },
    [ValkTalkPostAction.delete]: {
        [ValkTalkFeedType.valk]: 'delete-valk-feed-message',
        [ValkTalkFeedType.hotel]: 'delete-hotel-feed-message',
    },
}

export const valkTalkPermissions: PermissionSlug[] = Object.values(feedTypePermissions)
    .flatMap(permissions => Object.values(permissions))

export const getCurrentTenant = (): string => process.env.REACT_APP_TENANT || window.location.hostname

// The hotel in production (`akersloot.manager.valkplanner.nl`), the acceptance hotel in acceptance
// (`valk-manager-app.acceptance.kubernetes.pwstaging.tech`) and the whole tenant otherwise
const getTenantFeedName = (labels: string[]): string => {
    if (labels.length > 2 && labels[1] === 'manager') {
        return labels[0]
    }

    if (labels.length > 1 && labels[0].includes('manager-')) {
        return ACCEPTANCE_HOTEL_FEED_ID
    }

    return labels.join('.')
}

// Must match the hotel feed ids the back-end creates. Stream feed ids may only contain letters, numbers, dashes and
// underscores, so the name is normalized.
export const transformTenantToFeedId = (tenant: string): string => getTenantFeedName(tenant.trim().toLowerCase().split('.'))
    .replace(/[^a-z0-9_-]+/g, '-')
    .replace(/^-+|-+$/g, '')

export const getFeedGroupAndId = (feedType: ValkTalkFeedType, tenant: string): [string, string] => {
    if (feedType === ValkTalkFeedType.valk) {
        return [VALK_FEED_GROUP, VALK_FEED_ID]
    }

    return [HOTEL_FEED_GROUP, transformTenantToFeedId(tenant)]
}

export const getFeedTypeFromFids = (fids: string[]): ValkTalkFeedType | undefined => {
    const groups = fids.map(fid => fid.split(':')[0])

    if (groups.includes(VALK_FEED_GROUP)) {
        return ValkTalkFeedType.valk
    }

    if (groups.includes(HOTEL_FEED_GROUP)) {
        return ValkTalkFeedType.hotel
    }

    return undefined
}

export const getPermittedFeedTypes = (
    permissions: Permission[],
    action = ValkTalkPostAction.add,
): ValkTalkFeedType[] => (
    Object.values(ValkTalkFeedType).filter(feedType => checkPermission(permissions, feedTypePermissions[action][feedType]))
)

export const hasValkTalkPostContent = (formData: Pick<ValkTalkPostFormData, 'title' | 'text' | 'images'>): boolean => (
    !! formData.title.trim() || !! formData.text.trim() || !! formData.images.length
)

export const moveArrayElement = <T>(array: T[], fromIndex: number, toIndex: number): T[] => {
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0 || fromIndex >= array.length || toIndex >= array.length) {
        return array
    }

    const copy = [...array]
    const [element] = copy.splice(fromIndex, 1)
    copy.splice(toIndex, 0, element)

    return copy
}

export const getValkTalkPostsFeedFilter = (month: Date, authorId?: string): Record<string, unknown> => {
    const start = startOfMonth(month)
    const end = addMonths(start, 1)

    return {
        $and: [
            { created_at: { $gte: start.toISOString() } },
            { created_at: { $lt: end.toISOString() } },
            ...(authorId ? [{ user_id: { $eq: authorId } }] : []),
        ],
    }
}

export const sortValkTalkPostsByNewest = (posts: ValkTalkPost[]): ValkTalkPost[] => (
    [...posts].sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
)

export const doesValkTalkPostMatchQuery = (post: ValkTalkPost, query: ValkTalkPostsQuery): boolean => {
    const start = startOfMonth(query.month)
    const end = addMonths(start, 1)

    return query.feedTypes.includes(post.feedType)
        && post.createdAt >= start
        && post.createdAt < end
        && (! query.authorId || post.author.id === query.authorId)
}

// Stream can take a moment before a new or changed post shows up in a feed read, so posts saved or deleted in this
// session are applied on top of the fetched posts. Saved posts replace their fetched version, as they're never older.
export const mergeSavedValkTalkPosts = (
    fetchedPosts: ValkTalkPost[],
    savedPosts: ValkTalkPost[],
    deletedPostIds: string[],
    query: ValkTalkPostsQuery,
): ValkTalkPost[] => {
    const savedPostIds = savedPosts.map(post => post.id)
    const otherPosts = fetchedPosts.filter(post => ! savedPostIds.includes(post.id))
    const matchingSavedPosts = savedPosts.filter(post => doesValkTalkPostMatchQuery(post, query))

    return sortValkTalkPostsByNewest([...otherPosts, ...matchingSavedPosts])
        .filter(post => ! deletedPostIds.includes(post.id))
}

export const getValkTalkPostDisplayTitle = (
    post: Pick<ValkTalkPost, 'title' | 'text'>,
    maxLength = VALK_TALK_POST_TITLE_FALLBACK_LENGTH,
): string => {
    const title = post.title.trim()

    if (title) {
        return title
    }

    const text = post.text.trim().replace(/\s+/g, ' ')

    if (text.length <= maxLength) {
        return text
    }

    const truncated = text.slice(0, maxLength)
    const lastSpaceIndex = truncated.lastIndexOf(' ')

    return `${(lastSpaceIndex > maxLength / 2 ? truncated.slice(0, lastSpaceIndex) : truncated).trimEnd()}…`
}

export const filterValkTalkPostsBySearch = (posts: ValkTalkPost[], search: string): ValkTalkPost[] => {
    const query = search.trim().toLowerCase()

    if (! query) {
        return posts
    }

    return posts.filter(post => `${post.title} ${post.text}`.toLowerCase().includes(query))
}

// Example on production: `utrecht.manager.valkplanner.nl` > `utrecht.valkplanner.nl`
// Example on acceptance: `valk-manager-app.acceptance.kubernetes.pwstaging.tech` > `valk-employee-app.acceptance.kubernetes.pwstaging.tech`
export const transformTenantToEmployeeAppHostname = (tenant: string): string | undefined => {
    if (tenant.includes('.manager.')) {
        return tenant.replace('.manager.', '.')
    }

    if (tenant.includes('manager-')) {
        return tenant.replace('manager-', 'employee-')
    }

    return undefined
}

export const getEmployeeAppPostUrl = (postId: string, tenant = getCurrentTenant()): string | undefined => {
    const employeeAppHostname = transformTenantToEmployeeAppHostname(tenant)

    if (! employeeAppHostname) {
        return undefined
    }

    return `https://${employeeAppHostname}/${EMPLOYEE_APP_NEWSFEED_PATH}?post=${encodeURIComponent(postId)}`
}
